import React from 'react';
import type { ApolloCache, Reference } from '@apollo/client';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { NOTIFICATIONS_BELL_COUNT_QUERY } from './notifications_bell_count_hook';

type NotificationStreamItem = NotificationsCenterQuery['notificationStream']['items'][number];
type NotificationAction = NotificationsCenterQuery['notificationActions']['actions'][number];

export const MARK_NOTIFICATION_STREAM_ITEMS_READ = gql(`
  mutation NotificationsCenterMarkStreamItemsRead($input: MarkNotificationStreamItemsReadInput!) {
    markNotificationStreamItemsRead(input: $input) {
      count
      readAt
    }
  }
`);

export const MARK_ALL_NOTIFICATION_STREAM_ITEMS_READ = gql(`
  mutation NotificationsCenterMarkAllStreamItemsRead {
    markAllNotificationStreamItemsRead {
      count
      readAt
    }
  }
`);

// The clicks that send these also navigate away, which cancels an ordinary request before
// the server records the read.
const SURVIVE_NAVIGATION = { fetchOptions: { keepalive: true } };

/**
 * The bell reads its own query, so the panel's total doesn't reach it through the shared
 * cache - both are stepped down here. Keyed on field name rather than a query document, so
 * it reaches every `after` variant `fetchMore` left behind.
 */
function decrementUnreadTotals(cache: ApolloCache<unknown>, by: number) {
  if (by <= 0) { return; }

  cache.modify({
    fields: {
      notificationStream(existing, { readField }) {
        const totalUnread = readField<number>('totalUnread', existing) ?? 0;

        return { ...existing, totalUnread: Math.max(0, totalUnread - by) };
      },
      notificationCounts(existing, { readField }) {
        const total = readField<number>('total', existing) ?? 0;

        return { ...existing, total: Math.max(0, total - by) };
      },
    },
  });
}

/**
 * Items are normalized, so writing `isRead` on the entity is all any list holding it needs.
 *
 * An item Apollo can't key bails before the totals move: `modify` with no `id` addresses
 * ROOT_QUERY, which carries no `isRead` to write, and decrementing anyway would take the
 * unread count down for an item still showing its dot.
 */
export function markStreamItemReadInCache(
  cache: ApolloCache<unknown>,
  item: NotificationStreamItem,
) {
  const id = cache.identify(item);
  if (!id) { return; }

  cache.modify({ id, fields: { isRead: () => true } });
  decrementUnreadTotals(cache, 1);
}

/**
 * `totalUnread` comes from the caller rather than the cache: the same response object can
 * be cached under several `after` args, and summing them would overshoot the bell.
 */
export function markAllStreamItemsReadInCache(
  cache: ApolloCache<unknown>,
  totalUnread: number,
) {
  const itemRefs: Reference[] = [];

  cache.modify({
    fields: {
      notificationStream(existing, { readField }) {
        itemRefs.push(...(readField<readonly Reference[]>('items', existing) ?? []));

        return { ...existing, totalUnread: 0 };
      },
      notificationCounts(existing, { readField }) {
        const total = readField<number>('total', existing) ?? 0;

        return { ...existing, total: Math.max(0, total - totalUnread) };
      },
    },
  });

  itemRefs.forEach(ref => {
    cache.modify({ id: cache.identify(ref), fields: { isRead: () => true } });
  });
}

/**
 * Marks one stream item read on click. Optimistic because the dot has to clear on the same
 * frame the item's `href` navigates away on. A failure rolls the write back on its own and
 * the item returns marked NEW, so nothing is surfaced for it.
 */
export function useMarkStreamItemRead() {
  const [markRead] = useMutation(MARK_NOTIFICATION_STREAM_ITEMS_READ, {
    refetchQueries: [{ query: NOTIFICATIONS_BELL_COUNT_QUERY }],
  });

  return React.useCallback((item: NotificationStreamItem) => {
    if (item.isRead) { return; }

    markRead({
      variables: { input: { ids: [item.id] } },
      context: SURVIVE_NAVIGATION,
      optimisticResponse: {
        markNotificationStreamItemsRead: {
          __typename: 'MarkNotificationStreamItemsReadPayload',
          count: 1,
          readAt: new Date().toISOString(),
        },
      },
      update: cache => markStreamItemReadInCache(cache, item),
    }).catch(() => {});
  }, [markRead]);
}

/**
 * Dismisses an action on click. Nothing is written to the cache: the click navigates away,
 * and the next fetch leaves the action out. Failures are swallowed as above.
 */
export function useMarkActionRead() {
  const [markRead] = useMutation(MARK_NOTIFICATION_STREAM_ITEMS_READ, {
    refetchQueries: [{ query: NOTIFICATIONS_BELL_COUNT_QUERY }],
  });

  return React.useCallback((action: NotificationAction) => {
    markRead({
      variables: { input: { ids: [action.id] } },
      context: SURVIVE_NAVIGATION,
    }).catch(() => {});
  }, [markRead]);
}

/** Clears the whole stream. Optimistic and swallowed on failure, as above. */
export function useMarkAllStreamItemsRead() {
  const [markAllRead] = useMutation(MARK_ALL_NOTIFICATION_STREAM_ITEMS_READ, {
    refetchQueries: [{ query: NOTIFICATIONS_BELL_COUNT_QUERY }],
  });

  return React.useCallback((totalUnread: number) => {
    markAllRead({
      optimisticResponse: {
        markAllNotificationStreamItemsRead: {
          __typename: 'MarkAllNotificationStreamItemsReadPayload',
          count: totalUnread,
          readAt: new Date().toISOString(),
        },
      },
      update: cache => markAllStreamItemsReadInCache(cache, totalUnread),
    }).catch(() => {});
  }, [markAllRead]);
}
